import { z } from "zod";

/**
 * Google connect hand-off (Calendar and Sheets share one OAuth client and one callback URL):
 * Google redirects to one fixed console URL, outside `/w/:ws`, so the workspace that started the
 * flow, and which integration it was, are parked in sessionStorage (same tab, no secrets: the
 * OAuth `state` and PKCE verifier are held server-side).
 */
export const PENDING_GOOGLE_CONNECT_KEY = "sagot:google-calendar-connect";
const MAX_AGE_MS = 15 * 60_000;
export const GOOGLE_CONNECT_INTEGRATIONS = ["google_calendar", "google_sheets"] as const;
export type GoogleConnectIntegration = (typeof GOOGLE_CONNECT_INTEGRATIONS)[number];

const pendingSchema = z.object({
  wsSlug: z.string().min(1).max(100),
  wsId: z.string().uuid(),
  integration: z.enum(GOOGLE_CONNECT_INTEGRATIONS).default("google_calendar"),
  startedAt: z.number().int(),
});
export type PendingGoogleConnect = z.infer<typeof pendingSchema>;

export function savePendingConnect(
  value: Omit<PendingGoogleConnect, "startedAt" | "integration"> & { integration?: GoogleConnectIntegration },
  now: number = Date.now(),
): void {
  try {
    const integration = value.integration ?? "google_calendar";
    window.sessionStorage.setItem(PENDING_GOOGLE_CONNECT_KEY, JSON.stringify({ ...value, integration, startedAt: now }));
  } catch {
    // Storage blocked (private mode): the callback shows the "start again" state.
  }
}

export function readPendingConnect(now: number = Date.now()): PendingGoogleConnect | null {
  try {
    const raw = window.sessionStorage.getItem(PENDING_GOOGLE_CONNECT_KEY);
    if (!raw) return null;
    const parsed = pendingSchema.safeParse(JSON.parse(raw));
    if (!parsed.success || now - parsed.data.startedAt > MAX_AGE_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

export function clearPendingConnect(): void {
  try {
    window.sessionStorage.removeItem(PENDING_GOOGLE_CONNECT_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}
